import get from 'lodash/get';

import {
  DSC_SCHEDULE,
  GIF_PLAN_OPTIONS,
  GIF_SCHEME_TYPES,
  MAX_DEATH_BENEFIT_AGE,
  MAX_DEATH_BENEFIT_AGE_75PLUS,
  MAX_LIF_AGE,
  MAX_RRIF_AGE,
  MIN_LIF_AGE,
  MIN_RRIF_AGE,
  MIN_LIF_RRIF_RATES,
  SALES_CHARGES,
  WITHDRAWAL_TYPES,
} from './constants';
import getLIFMaxRates from './lif-max-rates';
import { getAge, monthsDiff } from '../time';
import { FREQUENCIES } from '../constants';
import { calculateCompoundReturns, trimRollingData, validateRollings } from '../calculation-utils';
import Holding from '@/types/holding';
import Fund from '@/types/fund';
import LocalizedAllocationDetails from '@/types/localized-allocation-details';
import BMOGIFScenarioData from '@/types/bmo-gif/bmo-gif-scenario-data';
import SalesCharge from '@/types/bmo-gif/sales-charge';
import Withdrawal from '@/types/bmo-gif/withdrawal';
import BMOGIFPortfolio from '@/types/bmo-gif/bmo-gif-portfolio';
import BMOPortfolioData from '@/types/bmo-gif/bmo-portfolio-data';
import BMOGIFScenario from '@/types/bmo-gif/bmo-gif-scenario';

const getRRIFRate = (age: number) => {
  if (age < MIN_RRIF_AGE) {
    return 0;
  }
  if (age >= MAX_RRIF_AGE) {
    return MIN_LIF_RRIF_RATES[MAX_RRIF_AGE];
  }
  return MIN_LIF_RRIF_RATES[age];
};

const getLIFMinRate = (age: number) => {
  if (age < MIN_LIF_AGE) {
    return 0;
  }
  if (age >= MAX_LIF_AGE) {
    return MIN_LIF_RRIF_RATES[MAX_LIF_AGE];
  }
  return MIN_LIF_RRIF_RATES[age];
};

const getLIFMaxRate = (age: number, province: string) => {
  if (age < MIN_LIF_AGE) {
    return 0;
  }

  const rates = getLIFMaxRates() || {};
  const provinceRates = rates[province];

  if (!provinceRates) {
    return 0;
  }

  if (age >= MAX_LIF_AGE) {
    return provinceRates[MAX_LIF_AGE];
  }

  return provinceRates[age];
};

const getMaxDeathBenefitAge = (series: string) => {
  if (series === GIF_PLAN_OPTIONS.GIF75100PLUS) {
    return MAX_DEATH_BENEFIT_AGE_75PLUS;
  }
  return MAX_DEATH_BENEFIT_AGE;
};

/**
 * Gets the contract maturity date
 * @param {number} term the term length in years
 * @param {Date} purchaseDate the date the policy was purchased
 */
export const getMaturityDate = (term: number, purchaseDate: Date) => new Date(term + purchaseDate.getUTCFullYear(), 11, 31);

/**
 * Calculates the gross return of the portfolio
 * @param {[number, number][]}  marketReturn the market return
 * @param {number} numOfMonths the current number of months
 * @returns {number} the gross return
 */
const calculateGrossReturn = (marketReturn: [number, number][], numOfMonths: number): number => {
  if (marketReturn.length === 0) {
    return 0;
  }
  let offset = numOfMonths - 1;
  if (offset >= marketReturn.length) {
    offset %= marketReturn.length;
  }
  return marketReturn[offset][1];
};

/**
 * Calculates the average return from the market (annualized)
 * @param {[number, number][]} marketReturn the market return (based on projection basis)
 */
export const calculateMarketReturn = (marketReturn: [number, number][]) => {
  if (marketReturn.length === 0) {
    return 0;
  }
  const average = marketReturn.slice(-60).reduce((acc, curr) => acc + curr[1], 0) / 60;
  const annualizedReturn = (average / 100 + 1) ** 12 - 1;
  return annualizedReturn;
};

/**
 * Calculates the average gross for the portfolio (annualized)
 * @param {BMOGIFScenarioData[]} data the scenario data
 */
export const calculateAnnualizedNetReturn = (data: BMOGIFScenarioData[]) => {
  if (data.length === 0) {
    return 0;
  }
  const netReturns = data.map(datum => datum.netReturn);
  return 100 * calculateCompoundReturns(netReturns, netReturns.length / 12).annualizedReturn;
};

/**
 * Calculate the net return
 * @param {number} grossReturn the gross return percentage
 * @param {number} mer the mer percent
 * @param {Date} date the date to compare against
 * @param {Date} birthday the client's birthday
 * @param {boolean} deathBenefitReset if the death benefit reset is set
 * @param {number} dbRider additional fee for resets
 * @param {number} customReturnAmount custom rate of return amount (the calculation differs based on that)
 */
const calculateNetReturn = (
  series: string,
  grossReturn: number,
  mer: number,
  date: Date,
  birthday: Date,
  deathBenefitReset: boolean = false,
  dbRider: number = 0,
  customReturnAmount?: number,
): number => {
  let totalFee = mer;
  const years = birthday.getUTCFullYear() + getMaxDeathBenefitAge(series);
  const threshold = new Date(years, birthday.getUTCMonth(), birthday.getUTCDate());
  threshold.setUTCHours(0, 0, 0, 0);
  if (date.getTime() < threshold.getTime() && deathBenefitReset) {
    totalFee = mer + dbRider;
  }
  if (customReturnAmount != null) {
    return 100 * ((1 + customReturnAmount / 100 - totalFee / 100) ** (1 / 12) - 1);
  }
  return 100 * ((1 + grossReturn / 100) * (1 - totalFee / 100 / 12) - 1);
};

/**
 * Calculate the sales charge amount when Deferred Sales Charge is selected
 * @deprecated DSC has been banned in Canada since June 1, 2022 -- the option in the UI is removed, but we keep this method for existing comparisons
 */
const calculateSalesCharge = (salesCharge: SalesCharge, policyYear: number, portfolioValue: number, withdrawalAmount: number) => {
  if (salesCharge.type === SALES_CHARGES.DSC) {
    const percentage = (DSC_SCHEDULE[policyYear] || 0) / 100;
    const diff = withdrawalAmount * 12 - 0.1 * portfolioValue;
    if (diff <= 0) {
      return 0;
    }
    return (diff / 12) * percentage;
  }
  return 0;
};

const calculatePortfolioValue = (prevValue: BMOGIFScenarioData) => {
  const { netReturn, salesCharge, netWithdrawal, portfolioValue, netDeposit } = prevValue;
  const returnPercent = 1 + netReturn / 100;
  const value = (portfolioValue - netWithdrawal - salesCharge + netDeposit) * returnPercent;
  return Math.max(value, 0);
};

/**
 * Calculates the monthly RRIF
 * @param {number} startOfYearAge the age at the start of the year
 * @param {number} startOfYearValue the value of the fund at the start of the year
 */
const calculateRRIFMin = (startOfYearAge: number, startOfYearValue: number) => {
  const rate = getRRIFRate(startOfYearAge) / 100;
  return (rate * startOfYearValue) / 12;
};

/**
 * Calculates the monthly LIF Minimum
 * @param {number} startOfYearAge the age at the start of the year
 * @param {number} startOfYearValue the value of the fund at the start of the year
 */
const calculateLIFMinimum = (startOfYearAge: number, startOfYearValue: number) => {
  const rate = getLIFMinRate(startOfYearAge) / 100;
  return (rate * startOfYearValue) / 12;
};

/**
 * Calculates the monthly LIF Maximum
 * @param {number} startOfYearAge the age at the start of the year
 * @param {number} startOfYearValue the value of the fund at the start of the year
 */
const calculateLIFMaximum = (startOfYearAge: number, startOfYearValue: number, province: string) => {
  const rate = getLIFMaxRate(startOfYearAge, province) / 100;
  return (rate * startOfYearValue) / 12;
};

const calculateInitialValue = (initialDeposit: number, salesCharge: SalesCharge) => {
  if (salesCharge.type === SALES_CHARGES.FEL) {
    return initialDeposit * (1 - salesCharge.percent / 100);
  }
  return initialDeposit;
};

/**
 * Gets the transaction amount for withdraws and deposits
 * @param {number} start the start age or policy year
 * @param {number} end the end age or policy year
 * @param {number} currentValue the current Age or policy Year
 * @param {number} frequency frequency of transaction
 * @param {number} amount the amount in dollars
 * @param {number} startMonth the startMonth
 * @param {number} endMonth the endMonth
 * @param {number} currentPolicyMonth the current policy month
 *
 */
const getSystematicTransactionAmount = (
  start: number,
  end: number,
  currentValue: number,
  frequency: string,
  amount: number,
  startMonth: number = 1,
  endMonth: number = 12,
  currentPolicyMonth: number,
) => {
  const isValid =
    currentValue >= start &&
    currentValue <= end &&
    (currentValue !== start || currentPolicyMonth >= startMonth) &&
    (currentValue !== end || currentPolicyMonth <= endMonth);
  if (isValid) {
    if (frequency === FREQUENCIES.MONTHLY) {
      return amount;
    }
    if (frequency === FREQUENCIES.ANNUALLY && currentPolicyMonth % 12 === 0) {
      return amount;
    }
    if (frequency === FREQUENCIES.SEMI_ANNUALLY && currentPolicyMonth % 6 === 0) {
      return amount;
    }
    if (frequency === FREQUENCIES.QUARTERLY && currentPolicyMonth % 3 === 0) {
      return amount;
    }
    if (frequency === FREQUENCIES.WEEKLY) {
      return amount * (52 / 12);
    }
    if (frequency === FREQUENCIES.BI_WEEKLY) {
      return amount * (52 / 12 / 2);
    }
  }
  return 0;
};

/**
 * Gets the total withdrawal amount per row
 * @param {number} lumpSumMonth the month where a lumpsum occurs
 * @param {Withdrawal} withdrawalSettings the withdrawal settings
 * @param {number} currentValue the current age or policy year
 * @param {number} startOfYearAge the age at the start of the calendar year
 * @param {number} startOfYearValue start of year fund value
 * @param {number} policyMonth the overall policy month
 * @param {number} currentPolicyMonth the current policy month of the year (1-12)
 */
const getTotalWithdrawal = (
  lumpSumMonth: number,
  withdrawalSettings: Withdrawal,
  currentValue: number,
  startOfYearAge: number,
  startOfYearValue: number,
  policyMonth: number,
  currentPolicyMonth: number,
  province: string,
) => {
  const { systematic, lumpSum } = withdrawalSettings;
  let lumpSumAmount = 0;
  let systematicAmount = 0;
  if (lumpSum.amount) {
    lumpSumAmount = lumpSumMonth === policyMonth ? lumpSum.amount : 0;
  }
  if (systematic && systematic.type) {
    if (systematic.type === WITHDRAWAL_TYPES.CUSTOM) {
      const end = systematic.scheme === GIF_SCHEME_TYPES.YEAR ? systematic.end - 2 : systematic.end;
      const start = systematic.scheme === GIF_SCHEME_TYPES.YEAR ? (systematic.start || 1) - 1 : systematic.start || 0;
      systematicAmount = getSystematicTransactionAmount(
        start,
        end,
        currentValue,
        systematic.frequency,
        systematic.amount,
        systematic.startMonth,
        systematic.endMonth,
        currentPolicyMonth,
      );
    } else if (
      systematic.type === WITHDRAWAL_TYPES.RRIF &&
      systematic.start &&
      systematic.scheme === GIF_SCHEME_TYPES.AGE &&
      startOfYearAge >= systematic.start
    ) {
      systematicAmount = calculateRRIFMin(startOfYearAge, startOfYearValue);
    } else if (
      systematic.type === WITHDRAWAL_TYPES.LIF_MAX &&
      systematic.start &&
      systematic.scheme === GIF_SCHEME_TYPES.AGE &&
      startOfYearAge >= MIN_LIF_AGE
    ) {
      // With the LIF Max scenario, we pad the systematic withdrawal years that are keyed in with the RRIF/LIF minimum values if the year is above the minimum
      // start age defined by the government. In theory we should do the same with WITHDRAWAL_TYPES.RRIF but the customer didn't want to expand scope in V4.0
      if (startOfYearAge >= systematic.start && (!systematic.end || startOfYearAge <= systematic.end)) {
        systematicAmount = calculateLIFMaximum(startOfYearAge, startOfYearValue, province);
      } else {
        systematicAmount = calculateLIFMinimum(startOfYearAge, startOfYearValue);
      }
    }
  }
  return systematicAmount + lumpSumAmount;
};

export const projectionBasisRolling = (equityPercent: number, equityBenchmark: [number, number][], fixedIncomeBenchmark: [number, number][]) => {
  if (equityBenchmark.length === 0 || fixedIncomeBenchmark.length === 0) {
    return [];
  }
  const start = equityBenchmark[0][0];
  const end = equityBenchmark[equityBenchmark.length - 1][0];
  // Trim the fixed income benchmark to the same length of the equity benchmark
  const trimmedFixedIncomeBenchmark = trimRollingData(fixedIncomeBenchmark, start, end);
  if (equityBenchmark.length !== trimmedFixedIncomeBenchmark.length) {
    return [];
  }
  if (!validateRollings(equityBenchmark, trimmedFixedIncomeBenchmark, equityBenchmark.length)) {
    return [];
  }
  const fixedIncomePercent = 1 - equityPercent;
  return equityBenchmark.slice().map((rolling, i) => {
    const item = rolling.slice() as [number, number];
    item[1] = equityPercent * item[1] + fixedIncomePercent * trimmedFixedIncomeBenchmark[i][1];
    return item;
  });
};

/**
 * Gets the age at the start of the year
 * @param dob the date of birth
 * @param date the current Date
 */
const getStartOfYearAge = (dob: Date, date: Date) => {
  const startOfYearAge = getAge(dob, undefined, new Date(date.getUTCFullYear(), 0, 1));
  return startOfYearAge;
};

/**
 * Gets the number of months in the term
 * @param {Date} startDate the start date the policy
 * @param {Date} endDate the end date of the policy
 */
const getTermMonths = (startDate: Date, endDate: Date) => {
  const endOfMonth = new Date(startDate.getFullYear(), startDate.getMonth() + 1, 0);
  endOfMonth.setHours(0, 0, 0, 0);
  const offset = -(endOfMonth.getTimezoneOffset() / 60) * 3600 * 1000;
  return monthsDiff(new Date(endOfMonth.getTime() + offset), endDate) + 1;
};

const getCurrentAge = (birthday: Date, prevDate: Date, previousAge: number) => {
  if (birthday.getUTCMonth() === prevDate.getUTCMonth()) {
    return previousAge + 1;
  }
  return previousAge;
};

const getCurrentDate = (months: number, purchaseDate: Date) => {
  const start = new Date(purchaseDate.getUTCFullYear(), purchaseDate.getUTCMonth());
  start.setMonth(start.getMonth() + months);
  return new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), 0));
};

const getLumpMonth = (policyMonth: number, currentPolicyMonth: number, currentValue: number, targetValue: number, targetMonth: number = 12) => {
  if (currentPolicyMonth === targetMonth && currentValue === targetValue) {
    return policyMonth;
  }
  return -1;
};

const calculateInitialMG = (initialDeposit: number, series: string) => {
  if (series === GIF_PLAN_OPTIONS.GIF100100) {
    return initialDeposit;
  }
  return initialDeposit * 0.75;
};

const calculateInitialDBG = (initialDeposit: number, series: string, age: number) => {
  if (series === GIF_PLAN_OPTIONS.GIF7575) {
    return initialDeposit * 0.75;
  }
  if (series === GIF_PLAN_OPTIONS.GIF75100PLUS) {
    if (age >= 85) {
      return initialDeposit * 0.75;
    }
    return initialDeposit;
  }
  if (age >= 80) {
    return initialDeposit * 0.75;
  }
  return initialDeposit;
};

const calculateDepositDeathGuarantee = (series: string, amount: number, age: number) => {
  // 75/75 So always 75%
  if (series === GIF_PLAN_OPTIONS.GIF7575) {
    return amount * 0.75;
  }
  // 75/100 Plus So 100% if less than 85 years old
  if (series === GIF_PLAN_OPTIONS.GIF75100PLUS && age < 85) {
    return amount;
  }
  // 100/100 or 75/100 So 100% if less than 80 years old
  if (age < 80) {
    return amount;
  }
  return amount * 0.75;
};

const calculateDepositMaturityGuarantee = (series: string, amount: number, calendarYear: number, term: number) => {
  if (series === GIF_PLAN_OPTIONS.GIF100100) {
    if (term - calendarYear < 15) {
      return amount * 0.75;
    }
    return amount;
  }
  return amount * 0.75;
};

const isMGREffective = (calendarYear: number, term: number) => term - calendarYear >= 10;

const calculateMaturityGuarantee = (prevValue: BMOGIFScenarioData, series: string, portfolioValue: number, mgrEffective: boolean) => {
  if (portfolioValue === 0) {
    return 0;
  }
  const amount = prevValue.maturityGuarantee - (prevValue.withdrawal / prevValue.portfolioValue) * prevValue.maturityGuarantee;
  const mg = Number.isFinite(amount) ? amount + prevValue.depositMaturityGuarantee : prevValue.depositMaturityGuarantee;
  if (mgrEffective && series === GIF_PLAN_OPTIONS.GIF100100) {
    return Math.max(mg, portfolioValue);
  }
  return mg;
};

const isDBREffective = (age: number, policyYear: number, calendarMonth: number, firstMonth: number, enhanced: boolean) => {
  const resetPeriod = enhanced ? 1 : 3; // Enhanced resets every year, otherwise every 3 years
  return age < 80 && (policyYear % resetPeriod === 0 || age === 79) && calendarMonth === firstMonth;
};

const calculateDeathBenefitGuarantee = (prevValue: BMOGIFScenarioData, series: string, portfolioValue: number, dbrEffective: boolean) => {
  if (portfolioValue === 0) {
    return 0;
  }
  const amount = prevValue.deathBenefitGuarantee - (prevValue.withdrawal / prevValue.portfolioValue) * prevValue.deathBenefitGuarantee;
  const dbg = Number.isFinite(amount) ? amount + prevValue.depositDeathGuarantee : prevValue.depositDeathGuarantee;
  if (series === GIF_PLAN_OPTIONS.GIF7575) {
    return dbg;
  }
  if (dbrEffective) {
    return Math.max(dbg, portfolioValue);
  }
  return dbg;
};

const calculateNetDeposit = (amount: number, salesCharge: SalesCharge) => {
  if (salesCharge.type === SALES_CHARGES.FEL) {
    return amount * (1 - salesCharge.percent / 100);
  }
  return amount;
};

/**
 * Calculate weighted average of fund values for property path
 * @param {Portfolio} portfolio Portfolio to be calculated
 * @param {Holding[]} data Data for holdings
 * @param {string} path the path of the property to calculate
 * @return {number} weighted average value
 */
const weightedAverage = (portfolio: BMOGIFPortfolio, data: Holding[], path: string) => {
  const { holdings } = portfolio;

  if (holdings.length === 0) {
    return undefined;
  }

  return holdings.reduce((total, holding) => {
    const item = data.find(d => d._id === holding.id);
    if (!item) {
      return total;
    }
    return total + (get(item, path, 0) * holding.weight) / 100;
  }, 0);
};

const calculateBMOGifPortfolioMER = (portfolio: BMOGIFPortfolio, data: Fund[]) => {
  const mer = weightedAverage(portfolio, data, 'mer');
  return mer || 0;
};

export const calculateBMOGifPortfolioDeathFee = (portfolio: BMOGIFPortfolio, data: Fund[], enhanced = false) => {
  let dbFee = weightedAverage(portfolio, data, 'deathBenefitFee') || 0;
  if (enhanced) {
    dbFee += weightedAverage(portfolio, data, 'deathBenefitFeeEnhanced') || 0;
  }
  return dbFee;
};

const getBMOPortfolioEquityPercentage = (portfolio: BMOGIFPortfolio, data: Holding[]) => {
  const { holdings } = portfolio;
  const allocations = holdings.reduce((acc: number, holding) => {
    const obj = data.find(d => d._id === holding.id);

    if (!obj) {
      return acc;
    }

    get(obj, 'fundPortfolio.allocations.asset', []).forEach((item: LocalizedAllocationDetails) => {
      if (!item || !item.name) {
        return;
      }
      const { name } = item;
      if (name['en-CA'] && name['en-CA'].toLowerCase().includes('equity')) {
        acc += (holding.weight / 100) * (item.weight / 100);
      }
    });

    return acc;
  }, 0);

  return allocations;
};

export const getCustomReturnRolling = (customReturn: number): [number, number][] => {
  const monthlyData: [number, number][] = [];
  const periodicalRate = 100 * ((1 + customReturn / 100) ** (1 / 12) - 1);
  const startDate = new Date('1986-01-31');
  for (let i = 0; i < 414; i++) {
    monthlyData.push([getCurrentDate(i + 1, startDate).getTime(), periodicalRate]);
  }
  return monthlyData;
};

export const calculateBMOGifPortfolio = (portfolio: BMOGIFPortfolio, data: BMOPortfolioData): BMOGIFPortfolio => {
  const { funds, fixedIncome, benchmark } = data;
  const equityPercent = getBMOPortfolioEquityPercentage(portfolio, funds);
  const marketReturn = projectionBasisRolling(equityPercent, benchmark, fixedIncome);
  const averageMarketReturn = calculateMarketReturn(marketReturn) * 100;
  return {
    ...portfolio,
    mer: calculateBMOGifPortfolioMER(portfolio, funds),
    deathBenefitFee: calculateBMOGifPortfolioDeathFee(portfolio, funds, portfolio.deathGuaranteeResetEnhanced),
    equityPercent,
    marketReturn: [...marketReturn].reverse(),
    averageMarketReturn,
  };
};

export const calculateScenario = (scenario: BMOGIFScenario, marketReturn: [number, number][], customReturnAmount?: number) => {
  const scenarioData: BMOGIFScenarioData[] = [];
  const {
    mer,
    purchaseDate,
    series,
    initialValue,
    maturityDate,
    deathBenefitReset,
    deathBenefitFee,
    deathBenefitResetEnhanced,
    salesCharge,
    dob,
    term,
    withdrawal,
    deposit,
  } = scenario;

  const termMonths = getTermMonths(purchaseDate, maturityDate);
  const purchaseAge = getAge(dob, undefined, purchaseDate) || 0;
  const initialPortfolioValue = calculateInitialValue(initialValue, salesCharge);
  const initialDBG = calculateInitialDBG(initialValue, series, purchaseAge);
  const initialMG = calculateInitialMG(initialValue, series);
  const end = deposit.scheme === GIF_SCHEME_TYPES.YEAR ? deposit.end - 2 : deposit.end;
  const start = deposit.scheme === GIF_SCHEME_TYPES.YEAR ? (deposit.start || 1) - 1 : deposit.start;
  let currentPolicyMonth = 0;
  for (let i = 0; i <= termMonths; i++) {
    const currentDate = getCurrentDate(i + 1, purchaseDate);
    const grossReturn = calculateGrossReturn(marketReturn, i + 1);
    const calendarYear = currentDate.getUTCFullYear() - purchaseDate.getUTCFullYear();
    const calendarMonth = currentDate.getUTCMonth() + 1;
    const startYearAge = getStartOfYearAge(dob, currentDate) || 0;
    const currentAge = i === 0 ? purchaseAge : getCurrentAge(dob, scenarioData[i - 1].date, scenarioData[i - 1].age);
    const policyYear = getAge(purchaseDate, undefined, currentDate) || 0;
    const policyMonth = i + 1;
    currentPolicyMonth++;
    if (currentPolicyMonth > 12) {
      currentPolicyMonth = 1;
    }

    let lumpSumMonth = -1;
    if (withdrawal) {
      if (withdrawal.lumpSum.scheme === GIF_SCHEME_TYPES.YEAR) {
        lumpSumMonth = getLumpMonth(policyMonth, currentPolicyMonth, policyYear, withdrawal.lumpSum.at - 1, withdrawal.lumpSum.month);
      }
      if (withdrawal.lumpSum.scheme === GIF_SCHEME_TYPES.AGE) {
        lumpSumMonth = getLumpMonth(policyMonth, currentPolicyMonth, currentAge, withdrawal.lumpSum.at);
      }
    }

    const withdrawalSchemeValue = withdrawal && withdrawal.systematic.scheme === GIF_SCHEME_TYPES.AGE ? currentAge : policyYear;
    const depositSchemeValue = deposit.scheme === GIF_SCHEME_TYPES.AGE ? currentAge : policyYear;
    const endOfYearValue = calendarYear === 0 ? initialPortfolioValue : scenarioData[i - calendarMonth].portfolioValue;
    let startOfYearValue = 0;
    if (calendarYear === 0) {
      startOfYearValue = initialPortfolioValue;
    } else if (calendarYear > 0) {
      const startOfYearIndex = i - calendarMonth;
      startOfYearValue = calculatePortfolioValue(scenarioData[startOfYearIndex]);
    }
    const portfolioValue = i === 0 ? initialPortfolioValue : calculatePortfolioValue(scenarioData[i - 1]);
    let withdrawalAmount =
      (withdrawal &&
        i !== termMonths &&
        getTotalWithdrawal(
          lumpSumMonth,
          withdrawal,
          withdrawalSchemeValue,
          startYearAge,
          startOfYearValue,
          policyMonth,
          currentPolicyMonth,
          scenario.province,
        )) ||
      0;
    if (withdrawalAmount > portfolioValue) {
      withdrawalAmount = 0;
    }
    const salesChargeAmount = calculateSalesCharge(salesCharge, policyYear, endOfYearValue, withdrawalAmount);
    const netWithdrawal = withdrawalAmount - salesChargeAmount;

    const depositAmount =
      i !== termMonths
        ? getSystematicTransactionAmount(start, end, depositSchemeValue, deposit.frequency, deposit.amount, undefined, undefined, currentPolicyMonth)
        : 0;
    const netDeposit = calculateNetDeposit(depositAmount, salesCharge);
    const isMGRMonth = isMGREffective(calendarYear, term);
    const maturityGuarantee = i === 0 ? initialMG : calculateMaturityGuarantee(scenarioData[i - 1], series, portfolioValue, isMGRMonth);
    const isDBRMonth = deathBenefitReset && isDBREffective(currentAge, policyYear, calendarMonth, purchaseDate.getUTCMonth() + 1, deathBenefitResetEnhanced);
    const deathBenefitGuarantee = i === 0 ? initialDBG : calculateDeathBenefitGuarantee(scenarioData[i - 1], series, portfolioValue, isDBRMonth);
    const rowData: BMOGIFScenarioData = {
      date: currentDate,
      calendarYear,
      calendarMonth,
      portfolioValue,
      policyYear,
      age: currentAge,
      policyMonth,
      startYearAge,
      grossReturn,
      salesCharge: salesChargeAmount,
      netReturn: calculateNetReturn(series, grossReturn, mer, currentDate, dob, deathBenefitReset, deathBenefitFee, customReturnAmount),
      netDeposit,
      deposit: depositAmount,
      depositDeathGuarantee: calculateDepositDeathGuarantee(series, depositAmount, currentAge),
      depositMaturityGuarantee: calculateDepositMaturityGuarantee(series, depositAmount, calendarYear, term),
      withdrawal: withdrawalAmount,
      netWithdrawal,
      maturityGuarantee,
      deathBenefitGuarantee,
    };
    scenarioData.push(rowData);
  }
  return scenarioData;
};
